import {
  Avatar,
  Box,
  Button,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Typography,
  useTheme,
} from "@mui/material";
import {NavLink, useNavigate} from "react-router-dom";
import {Fragment} from "react";
import packageInfo from "../../package.json";
import mfeLogo from "../assets/images/mfe-logo.svg";
import signOutIcon from "../assets/images/sign-out-icon.svg";
import {useCurrentUser} from "../hooks/useCurrentUser";
import {useLogout} from "../hooks/useLogout";
import routes from "../routes";
import {getCurrentUser, isAuthenticated} from "../util/AuthUtil";
import currentUserIcon from "../assets/images/current-user-icon@3x.png";

const drawerWidth = 230;

const Sidebar = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const logout = useLogout();
  const [currentUser, _] = useCurrentUser();
  // const accountModelRef = useRef();
  // function handleSignOut() {
  //   clearCurrentUser();
  //   navigate("/sign-in");
  // }

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          boxSizing: "border-box",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 0.8,
          py: 1,
        }}
      >
        <Box
          component="img"
          src={mfeLogo}
          alt="MFE Logo"
          sx={{
            height: 50, // Set the height of the logo to 100px
            ml: 0, // Align the logo to the left
          }}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            height: 50, // Match the height of the logo
          }}
        >
          <Typography
            variant="h6"
            sx={{
              ml: 2,
              fontWeight: 500,
            }}
          >
            MFE Admin Portal
          </Typography>
          <Typography
            variant="body1"
            sx={{
              ml: 2,
              fontSize: "0.875rem", // Match body1 size
            }}
          >
            {`v${packageInfo.version}`}
          </Typography>
        </Box>
      </Box>
      <Divider/>
      <List sx={{p: 0}}>
        {(() => {
          const visibleRoutes = routes.filter(
            ({key, visibleRepTypes}) =>
              key !== "sign-in" && visibleRepTypes?.includes(currentUser?.repType?.id)
          );
          let lastSection = null;
          return visibleRoutes.map(({name, icon, key, path, section}) => {
            const showSectionHeader = section && section !== lastSection;
            lastSection = section ?? null;
            return (
              <Fragment key={key}>
                {showSectionHeader && (
                  <Typography
                    sx={{
                      px: 1.5,
                      pt: 1.5,
                      pb: 0.5,
                      fontSize: "0.6875rem",
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: "text.disabled",
                    }}
                  >
                    {section}
                  </Typography>
                )}
                <ListItem sx={{p: 0}}>
                  <NavLink
                    to={path}
                    style={({isActive}) => ({
                      textDecoration: "none",
                      color: isActive ? "white" : "black",
                      width: "100%",
                      backgroundColor: isActive ? theme.palette.secondary.main : "white",
                    })}
                  >
                    <ListItemButton
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        py: 1,
                        px: 0.8,
                      }}
                    >
                      <ListItemAvatar
                        sx={{
                          p: 0.5,
                          width: 24,
                          height: 24,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center", // Center the logo vertically
                        }}
                      >
                        <Avatar src={icon} alt={name} sx={{width: 24, height: 24}}/> {/* Smaller icon */}
                      </ListItemAvatar>
                      <ListItemText
                        primary={name}
                      />
                    </ListItemButton>
                  </NavLink>
                </ListItem>
              </Fragment>
            );
          });
        })()}
      </List>

      <Divider/>
      {/* Moved the current user section and the sign-out button to the bottom of the Sidebar */}
      {isAuthenticated() ? (
        <Box
          sx={{
            mt: "auto", // Push to the bottom
          }}
        >
          <Divider/>

          <Box
            sx={{
              display: "flex",
              alignItems: "center", // Align icon and texts horizontally
              py: 1, // Add padding above and below the section
            }}
          >
            <Avatar
              src={currentUserIcon}
              alt="User Icon"
              sx={{
                width: 40,
                height: 40,
                ml: 2, // Add margin to the left of the icon
              }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center", // Center texts vertically
                ml: 2, // Add margin to the left of the icon
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 500,
                }}
              >
                {[getCurrentUser().firstName, getCurrentUser().lastName].filter(Boolean).join(" ")}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "gray",
                }}
              >
                {getCurrentUser().login}
              </Typography>
            </Box>
          </Box>
          <Divider />
          <Box
            sx={{
              display: "flex",
              flexDirection: "row", // Align icon and text horizontally
              justifyContent: "center", // Center horizontally
              alignItems: "center", // Center vertically
              py: 1, // Match padding with menu buttons
              px: 0.8, // Match padding with menu buttons
              backgroundColor: theme.palette.divider, // Keep the background color the same
              cursor: "pointer", // Add pointer cursor for clickability
            }}
            onClick={logout} // Handle the onClick logout action
          >
            <Avatar
              src={signOutIcon}
              alt="Sign Out Icon"
              sx={{
                width: 24, // Match icon size with menu buttons
                height: 24, // Match icon size with menu buttons
                mr: 1, // Add margin to the right of the icon
              }}
            />
            <Typography
              variant="body1"
              sx={{
                fontWeight: 500,
              }}
            >
              Sign Out
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            mt: "auto", // Push to the bottom
            p: 2,
          }}
        >
          <Button variant="contained" fullWidth onClick={() => navigate("/sign-in")}>
            <Typography>Sign In</Typography>
          </Button>
        </Box>
      )}
    </Drawer>
  );
};

export default Sidebar;
